import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, concatMap, defaultIfEmpty, filter, map, switchMap, take, tap, timer } from 'rxjs';
import { protectedResources } from '../auth-config';

export interface AskAiRequest {
  Query: string;
}

export interface AskAiResponse {
  success?: boolean;
  response?: string;
  result: string;
  dataSourcesUsed?: string[];
  [key: string]: any;
}

type AskAiCreateResponse = unknown;

@Injectable({
  providedIn: 'root',
})
export class AskAiService {
  private readonly url = protectedResources.apiAskAi.endpoint;
  private readonly guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  private readonly getDelayMs = 10000;
  private readonly maxPollingDurationMs = 180000;
  private readonly maxGetAttempts = Math.floor(this.maxPollingDurationMs / this.getDelayMs) + 1;

  constructor(private http: HttpClient) {}

  askAi(query: string): Observable<AskAiCreateResponse> {
    const request: AskAiRequest = { Query: query };
    return this.http.post<AskAiCreateResponse>(this.url, request);
  }

  askAiAndGetAnswer(query: string): Observable<AskAiResponse> {
    return this.askAi(query).pipe(
      map((payload) => this.extractRequestId(payload)),
      switchMap((requestId) => this.pollForAnswer(requestId))
    );
  }

  private pollForAnswer(requestId: string): Observable<AskAiResponse> {
    let lastResponse: AskAiResponse = {
      success: false,
      response: '',
      result: '',
    };

    return timer(0, this.getDelayMs).pipe(
      take(this.maxGetAttempts),
      concatMap(() =>
        this.http
          .get(this.url, {
            params: { id: requestId },
            responseType: 'text',
          })
          .pipe(map((answerPayload) => this.toAskAiResponse(answerPayload)))
      ),
      tap((response) => {
        lastResponse = response;
      }),
      filter((response) => !!response.result?.trim()),
      take(1),
      defaultIfEmpty(lastResponse)
    );
  }

  private toAskAiResponse(answerPayload: unknown): AskAiResponse {
    const source = this.asObject(answerPayload);
    const answerText = this.extractAnswerText(answerPayload);
    const successCandidate = source?.['success'] ?? source?.['Success'];
    const dataSourcesCandidate = source?.['dataSourcesUsed'] ?? source?.['DataSourcesUsed'];

    const success = typeof successCandidate === 'boolean'
      ? successCandidate
      : answerText.length > 0;

    const dataSourcesUsed = Array.isArray(dataSourcesCandidate)
      ? dataSourcesCandidate.filter((item): item is string => typeof item === 'string')
      : undefined;

    return {
      success,
      response: answerText,
      result: answerText,
      dataSourcesUsed,
    };
  }

  private asObject(payload: unknown): Record<string, unknown> | null {
    if (payload && typeof payload === 'object') {
      return payload as Record<string, unknown>;
    }

    if (typeof payload === 'string') {
      const trimmed = payload.trim();
      try {
        const parsed = JSON.parse(trimmed);
        return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
      } catch {
        return null;
      }
    }

    return null;
  }

  private extractRequestId(payload: unknown): string {
    const id = this.findIdInPayload(payload);
    if (!id) {
      throw new Error('AskAI did not return a valid request id.');
    }
    return id;
  }

  private findIdInPayload(payload: unknown): string | null {
    if (typeof payload === 'string') {
      const trimmed = payload.trim().replace(/^"|"$/g, '');
      if (this.guidPattern.test(trimmed)) {
        return trimmed;
      }

      try {
        return this.findIdInPayload(JSON.parse(trimmed));
      } catch {
        return null;
      }
    }

    if (Array.isArray(payload)) {
      for (const item of payload) {
        const id = this.findIdInPayload(item);
        if (id) {
          return id;
        }
      }
      return null;
    }

    if (payload && typeof payload === 'object') {
      const source = payload as Record<string, unknown>;
      const idCandidates = [source['id'], source['requestId'], source['askAiId'], source['queryId']];
      for (const candidate of idCandidates) {
        const id = this.findIdInPayload(candidate);
        if (id) {
          return id;
        }
      }

      if ('body' in source) {
        const id = this.findIdInPayload(source['body']);
        if (id) {
          return id;
        }
      }

      const keys = Object.keys(source);
      if (keys.length === 1 && this.guidPattern.test(keys[0])) {
        return keys[0];
      }

      for (const value of Object.values(source)) {
        const id = this.findIdInPayload(value);
        if (id) {
          return id;
        }
      }
    }

    return null;
  }

  private extractAnswerText(payload: unknown): string {
    if (typeof payload === 'string') {
      const trimmed = payload.trim();
      try {
        return this.extractAnswerText(JSON.parse(trimmed));
      } catch {
        return trimmed;
      }
    }

    if (payload && typeof payload === 'object') {
      const source = payload as Record<string, unknown>;
      const candidates = [
        source['response'],
        source['Response'],
        source['result'],
        source['Result'],
        source['answer'],
        source['Answer'],
        source['message'],
        source['Message'],
        source['body'],
      ];
      for (const candidate of candidates) {
        const text = this.extractAnswerText(candidate);
        if (text) {
          return text;
        }
      }
    }

    return '';
  }
}
